<template>
  <div class="bg-[#2f5c45] py-2 px-4 transition-all duration-300">
    <div class="container mx-auto flex justify-between items-center">
      <!-- PC端左侧空白区域保持不变 -->
      <div class="flex items-center space-x-4 hidden md:flex"></div>
      
      <!-- 移动端左侧空白区域 -->
      <div class="md:hidden"></div>
      
      <!-- PC端右侧内容 -->
      <div class="hidden md:flex items-center space-x-6">
        <div class="flex items-center space-x-2">
          <PhoneOutlined class="text-[#c3c6b3]" />
          <span class="text-[#c3c6b3] text-sm">{{
            t('topToolbar.contact.phone')
          }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <MailOutlined class="text-[#c3c6b3]" />
          <span class="text-[#c3c6b3] text-sm">{{
            t('topToolbar.contact.email', { pchar: '@' })
          }}</span>
        </div>
        <div class="flex items-center space-x-4">
          <a
            v-for="(social, index) in socials"
            :key="index"
            :href="social.link"
            class="text-[#c3c6b3] hover:text-gray-900 transition-colors"
          >
            <component :is="social.icon" />
          </a>
        </div>
        <div class="relative">
          <div
            @click="toggleLanguageDropdown"
            class="flex items-center space-x-2 cursor-pointer hover:text-white transition-colors"
          >
            <span class="text-[#c3c6b3] text-sm">{{
              t('topToolbar.language')
            }}</span>
            <DownOutlined class="text-[#c3c6b3] text-xs" />
          </div>
          <div
            v-if="showLanguageDropdown"
            class="absolute top-full right-0 mt-1 bg-white shadow-lg rounded-md py-2 z-20 min-w-[120px]"
          >
            <div
              v-for="lang in availableLanguages"
              :key="lang.code"
              @click="changeLanguage(lang.code)"
              class="px-4 py-2 hover:bg-gray-100 cursor-pointer whitespace-nowrap text-gray-800 transition-colors"
            >
              {{ lang.name }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- 移动端语言切换按钮 -->
      <div class="md:hidden relative z-60">
        <button 
          @click="toggleLanguageDropdown"
          class="text-[#c3c6b3] hover:text-white transition-colors flex items-center space-x-1 relative z-60"
        >
          <span class="text-sm">{{ getCurrentLanguageName() }}</span>
          <DownOutlined class="text-xs" />
        </button>
        <div
          v-if="showLanguageDropdown"
          class="absolute top-full right-0 mt-1 bg-white shadow-lg rounded-md py-2 z-70 min-w-[100px]"
        >
          <div
            v-for="lang in availableLanguages"
            :key="lang.code"
            @click="changeLanguage(lang.code)"
            class="px-3 py-2 hover:bg-gray-100 cursor-pointer whitespace-nowrap text-gray-800 text-sm transition-colors"
          >
            {{ lang.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup name="TopToolbar">
  import {
    PhoneOutlined,
    MailOutlined,
    DownOutlined,
    FacebookOutlined,
    TwitterOutlined,
    InstagramOutlined,
    LinkedinOutlined
  } from '@ant-design/icons-vue'
  import { ref, onMounted, nextTick, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { socialMediaIcons } from '@/config/imageResources'

  // 使用i18n
  const { t, locale } = useI18n()

  // 语言下拉菜单状态
  const showLanguageDropdown = ref(false)

  // 可用语言列表
  const availableLanguages = [
    { code: 'zh', name: '中文' },
    { code: 'en', name: 'English' }
  ]

  // 获取当前语言名称
  const getCurrentLanguageName = () => {
    const currentLang = availableLanguages.find(lang => lang.code === locale.value)
    return currentLang ? currentLang.name : '中文'
  }

  // 切换语言下拉菜单显示状态
  const toggleLanguageDropdown = () => {
    showLanguageDropdown.value = !showLanguageDropdown.value
  }

  // 切换语言
  const changeLanguage = async langCode => {
    try {
      // 先更新 locale
      locale.value = langCode
      // 保存到本地存储
      localStorage.setItem('locale', langCode)
      // 等待下一个 DOM 更新周期，确保语言切换生效
      await nextTick()
      // 关闭下拉菜单
      showLanguageDropdown.value = false
    } catch (error) {
      console.error('切换语言失败:', error)
    }
  }

  // 初始化语言设置
  const initLanguage = () => {
    try {
      const savedLocale = localStorage.getItem('locale')
      if (
        savedLocale &&
        availableLanguages.some(lang => lang.code === savedLocale)
      ) {
        locale.value = savedLocale
      }
    } catch (error) {
      console.error('初始化语言设置失败:', error)
    }
  }

  // 处理点击页面其他地方关闭下拉菜单
  const handleOutsideClick = e => {
    if (!e.target.closest('.relative')) {
      showLanguageDropdown.value = false
    }
  }

  // 在组件挂载后初始化语言
  onMounted(() => {
    initLanguage()
    // 添加全局点击事件监听器
    window.addEventListener('click', handleOutsideClick)
  })
  
  // 组件卸载时清理事件监听器
  onUnmounted(() => {
    window.removeEventListener('click', handleOutsideClick)
  })

  // 社交媒体链接
  const socials = [
    { icon: FacebookOutlined, link: socialMediaIcons.facebook || '#' },
    { icon: TwitterOutlined, link: socialMediaIcons.twitter || '#' },
    { icon: InstagramOutlined, link: socialMediaIcons.instagram || '#' },
    { icon: LinkedinOutlined, link: socialMediaIcons.linkedin || '#' }
  ]
</script>
<style scoped>
/* 响应式调整 */
@media (max-width: 768px) {
  .container {
    padding-left: 0;
    padding-right: 0;
  }
}

/* 确保下拉菜单在移动端有足够的层级 */
.relative {
  z-index: 10;
}

/* 移动端语言切换按钮确保在最高层级 */
@media (max-width: 768px) {
  .z-60 {
    z-index: 60;
  }
  
  .z-70 {
    z-index: 70;
  }
}
</style>
